import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type LegalTermSCurrent = {
  id: number;
  name: string;
  body: string;
  version: string;
  effective_from: string;
  effective_to?: string | null;
};

export type UpdateLegalTermsInput = {
  name?: string;
  body?: string;
  version?: string;
  effective_from?: string;
  effective_to?: string | null;
};

export const legalTermsApi = createApi({
  reducerPath: 'legalTermsApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['TermCurrent'],
  endpoints: (builder) => ({
    // GET /v1/legal/terms/current
    getTermsCurrent: builder.query<ApiResponseBase<LegalTermSCurrent>, void>({
      query: () => ({ url: '/v1/legal/terms/current', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Terms.Current'),
      providesTags: ['TermCurrent'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener los terminos legas del servicio',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'LegalTerms.Current'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'legalterms.current',
              endpointName: e?.endpointName || 'LegalTerms.Current',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/legal/terms/current
    updateTermsCurrent: builder.mutation<
      ApiResponseBase<{ id: number }>,
      UpdateLegalTermsInput
    >({
      query: (body) => ({ url: '/v1/legal/terms/current', method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Terms.UpdateCurrent'),
      invalidatesTags: ['TermCurrent'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar términos legales',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'LegalTerms.UpdateCurrent'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'legalterms.update',
              endpointName: e?.endpointName || 'LegalTerms.UpdateCurrent',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const { useGetTermsCurrentQuery, useUpdateTermsCurrentMutation } = legalTermsApi;
